import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, ShoppingBag, Store } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useCities } from "@/context/CityContext";
import { Logo } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const ROLES = [
  { value: "buyer", label: "I am a Buyer", hint: "I want to buy stock", icon: ShoppingBag },
  { value: "seller", label: "I am a Seller", hint: "I want to sell stock", icon: Store },
];

export default function VisitorEntry() {
  const nav = useNavigate();
  const { setVisitor } = useCities();
  const [role, setRole] = useState("");
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!role) { toast.error("Please select whether you are a Buyer or a Seller"); return; }
    const digits = mobile.replace(/\D/g, "");
    if (digits.length !== 10) { toast.error("Please enter a valid 10-digit mobile number"); return; }
    setBusy(true);
    try {
      await api.post("/visitors", { mobile: digits, type: role });
      setVisitor(digits, role);
      nav("/select-city");
    } catch (e) { toast.error(apiError(e)); setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-background grid place-items-center px-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8"><Logo onClick={() => {}} /></div>
        <div className="bg-card border rounded-3xl p-8 shadow-xl">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-primary bg-primary/10 px-3 py-1.5 rounded-full mb-5">
            <Phone className="h-3.5 w-3.5" /> Step 1 of 2
          </div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight">Welcome to Cleartile</h1>
          <p className="text-muted-foreground mt-2 text-sm font-medium">
            Tell us who you are and enter your mobile number to continue.
          </p>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">I am a *</label>
              <div className="mt-2 grid grid-cols-2 gap-3">
                {ROLES.map((r) => {
                  const Icon = r.icon;
                  const active = role === r.value;
                  return (
                    <button
                      key={r.value}
                      type="button"
                      data-testid={`visitor-role-${r.value}`}
                      onClick={() => setRole(r.value)}
                      className={`text-left rounded-xl border-2 p-3 transition-all ${active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"}`}
                    >
                      <div className={`h-9 w-9 rounded-lg grid place-items-center ${active ? "bg-primary text-white" : "bg-secondary text-muted-foreground"}`}>
                        <Icon className="h-5 w-5" />
                      </div>
                      <div className="mt-2 font-heading font-extrabold text-sm">{r.label}</div>
                      <div className="text-[11px] text-muted-foreground font-medium leading-tight">{r.hint}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="text-xs font-bold uppercase tracking-wide text-muted-foreground">Mobile number *</label>
              <div className="mt-2 flex items-center gap-2 rounded-xl border-2 focus-within:border-primary px-3 h-14 transition-colors">
                <span className="font-heading font-extrabold text-muted-foreground">+91</span>
                <span className="h-6 w-px bg-border" />
                <Input
                  data-testid="visitor-mobile-input"
                  type="tel"
                  inputMode="numeric"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  placeholder="10-digit mobile number"
                  className="border-0 shadow-none focus-visible:ring-0 text-lg font-bold px-0 h-full"
                />
              </div>
            </div>

            <Button data-testid="visitor-continue-btn" type="submit" disabled={busy} className="w-full h-14 rounded-xl text-base font-bold gap-2">
              {busy ? "Please wait…" : <>Continue <ArrowRight className="h-5 w-5" /></>}
            </Button>
          </form>
          <p className="mt-4 text-[11px] text-muted-foreground font-medium flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" /> Your number is only used to connect you with the right people. No spam.
          </p>
        </div>
      </div>
    </div>
  );
}
